import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api from "@/api";
import { KeyRound, Smartphone } from "lucide-react";

export default function Login() {
  const { t, lang, setLang, login, loginWithOtp } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("password"); // "password" | "otp"
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [demoOtp, setDemoOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!identifier || !password) return;
    setBusy(true);
    try { await login(identifier, password); nav("/"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const sendOtp = async () => {
    if (!identifier) return toast.error(t("email_or_phone"));
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/send", { identifier });
      setOtpSent(true);
      setDemoOtp(r.data.otp || "");
      toast.success(t("otp_sent"));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) return;
    setBusy(true);
    try { await loginWithOtp(identifier, otp); nav("/"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Invalid OTP"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-stretch">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzcGljZXMlMjBhZXN0aGV0aWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NTg2NjYwMHww&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover" alt="Indian spices" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white bg-gradient-to-t from-black/70 via-black/20 to-transparent w-full">
          <div className="font-heading font-bold text-5xl leading-tight tracking-tight">{t("app_name")}</div>
          <div className="mt-3 text-lg text-white/90 max-w-md">{t("tagline")}</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 rounded-2xl border border-stone-200 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#B45309] text-white grid place-items-center font-heading font-bold text-xl">
                {t("logo_letter")}
              </div>
              <div className="font-heading font-semibold text-xl">{t("app_name")}</div>
            </div>
            <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1">
              <button onClick={() => setLang("en")} data-testid="login-lang-en" className={`px-3 h-8 rounded-lg text-xs font-medium ${lang === "en" ? "bg-white shadow" : "text-stone-500"}`}>EN</button>
              <button onClick={() => setLang("gu")} data-testid="login-lang-gu" className={`px-3 h-8 rounded-lg text-xs font-medium font-gujarati ${lang === "gu" ? "bg-white shadow" : "text-stone-500"}`}>ગુ</button>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-bold text-stone-900 mb-1">{t("login")}</h1>
          <p className="text-stone-500 text-sm mb-6">{t("tagline")}</p>

          {/* Tabs */}
          <div className="grid grid-cols-2 gap-1 bg-stone-100 rounded-xl p-1 mb-6">
            <button
              onClick={() => setTab("password")}
              data-testid="tab-password"
              className={`h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${tab === "password" ? "bg-white shadow text-stone-900" : "text-stone-500"}`}
            >
              <KeyRound className="w-4 h-4" /> {t("password_tab")}
            </button>
            <button
              onClick={() => setTab("otp")}
              data-testid="tab-otp"
              className={`h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${tab === "otp" ? "bg-white shadow text-stone-900" : "text-stone-500"}`}
            >
              <Smartphone className="w-4 h-4" /> {t("otp_tab")}
            </button>
          </div>

          {tab === "password" && (
            <form onSubmit={submitPassword} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{t("email_or_phone")}</label>
                <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} data-testid="login-identifier" className="h-14 text-lg rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{t("password")}</label>
                <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" data-testid="login-password" className="h-14 text-lg rounded-lg" />
              </div>
              <Button type="submit" disabled={busy} data-testid="login-submit"
                      className="w-full h-14 text-lg rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold">
                {busy ? "…" : t("login")}
              </Button>
            </form>
          )}

          {tab === "otp" && (
            <form onSubmit={verifyOtp} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{t("email_or_phone")}</label>
                <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} data-testid="otp-identifier" className="h-14 text-lg rounded-lg" disabled={otpSent} />
              </div>
              {!otpSent && (
                <Button type="button" onClick={sendOtp} disabled={busy || !identifier} data-testid="otp-send"
                        className="w-full h-14 text-lg rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold">
                  {busy ? "…" : t("send_otp")}
                </Button>
              )}
              {otpSent && (
                <>
                  {demoOtp && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-xs text-green-800" data-testid="demo-otp-notice">
                      {t("demo_otp_notice")} <span className="font-mono font-bold text-base">{demoOtp}</span>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{t("otp")}</label>
                    <Input value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="otp-code" placeholder={t("otp_placeholder")} inputMode="numeric" maxLength={6} className="h-14 text-2xl font-mono tracking-widest text-center rounded-lg" />
                  </div>
                  <Button type="submit" disabled={busy || otp.length !== 6} data-testid="otp-verify"
                          className="w-full h-14 text-lg rounded-xl bg-[#166534] hover:bg-[#14532D] text-white font-semibold">
                    {busy ? "…" : t("verify_otp")}
                  </Button>
                  <button type="button" onClick={() => { setOtpSent(false); setOtp(""); setDemoOtp(""); }} data-testid="otp-resend"
                          className="w-full text-sm text-[#B45309] font-medium h-10">{t("resend_otp")}</button>
                </>
              )}
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
